import React, { useState, useCallback } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface Achievement {
  key: string;
  name: string;
}

export interface User {
  id: number;
  email: string;
  subscribed: boolean;
  diaryDaysCount: number;
  activitiesCount: number;
  createdAt: string;
}

export interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

export interface AdminUsersPageProps {
  // Data
  users: User[];
  achievements: Achievement[];

  // Stats
  totalUsers: number;
  usersToday: number;
  usersThisWeek: number;
  usersThisMonth: number;
  activeSubscribers: number;

  // Current filters/sort
  currentSearch: string;
  currentAchievement: string;
  currentSort: SortConfig | null;

  // URLs
  dashboardPath: string;
  usersPath: string;
  userPath: (id: number) => string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    searchPlaceholder: string;
    allAchievements: string;
    filterButton: string;
    clearFilters: string;
    viewButton: string;
    noUsersFound: string;
    noUsersYet: string;
    noUsersMatchingSearch: string;
    // Stats
    totalUsersLabel: string;
    todayLabel: string;
    thisWeekLabel: string;
    thisMonthLabel: string;
    activeSubscribersLabel: string;
    // Table
    idColumn: string;
    emailColumn: string;
    subscriptionColumn: string;
    diaryDaysColumn: string;
    activitiesColumn: string;
    registeredColumn: string;
    actionsColumn: string;
    activeLabel: string;
    freeLabel: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

interface SortableColumnProps {
  column: string;
  label: string;
  currentSort: SortConfig | null;
  onSort: (column: string) => void;
}

function SortableColumn({ column, label, currentSort, onSort }: SortableColumnProps) {
  const isActive = currentSort?.column === column;
  const icon = isActive
    ? currentSort?.direction === 'asc'
      ? 'bi-sort-up'
      : 'bi-sort-down'
    : 'bi-arrow-down-up';
  const className = isActive
    ? 'text-decoration-none text-primary fw-semibold'
    : 'text-decoration-none text-muted';

  return (
    <a
      href="#"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        onSort(column);
      }}
    >
      {label} <i className={`bi ${icon}`} />
    </a>
  );
}

function UserRow({
  user,
  userPath,
  labels,
}: {
  user: User;
  userPath: string;
  labels: AdminUsersPageProps['labels'];
}) {
  const createdDate = new Date(user.createdAt);
  const formattedDate = createdDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <tr>
      <td className="ps-4 fw-semibold">#{user.id}</td>
      <td>
        <div className="d-flex align-items-center">
          <i className="bi bi-person-circle text-muted me-2" />
          {user.email}
        </div>
      </td>
      <td>
        {user.subscribed ? (
          <span className="badge bg-success">
            <i className="bi bi-star-fill me-1" />
            {labels.activeLabel}
          </span>
        ) : (
          <span className="badge bg-secondary">{labels.freeLabel}</span>
        )}
      </td>
      <td>
        <span className="badge bg-info">{user.diaryDaysCount}</span>
      </td>
      <td>
        <span className="badge bg-success">{user.activitiesCount}</span>
      </td>
      <td>
        <small className="text-muted">{formattedDate}</small>
      </td>
      <td className="text-end pe-4">
        <a href={userPath} className="btn btn-sm btn-outline-primary">
          <i className="bi bi-eye me-1" />
          {labels.viewButton}
        </a>
      </td>
    </tr>
  );
}

function EmptyState({
  search,
  labels,
}: {
  search: string;
  labels: AdminUsersPageProps['labels'];
}) {
  return (
    <tr>
      <td colSpan={7} className="text-center py-5 text-muted">
        <i className="bi bi-people" style={{ fontSize: '3rem' }} />
        <p className="mt-2 mb-0">
          {search
            ? `${labels.noUsersMatchingSearch} "${search}"`
            : labels.noUsersYet}
        </p>
      </td>
    </tr>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminUsersPage({
  users,
  achievements,
  totalUsers,
  usersToday,
  usersThisWeek,
  usersThisMonth,
  activeSubscribers,
  currentSearch,
  currentAchievement,
  currentSort,
  dashboardPath,
  usersPath,
  userPath,
  labels,
}: AdminUsersPageProps) {
  const [search, setSearch] = useState(currentSearch);
  const [achievement, setAchievement] = useState(currentAchievement);

  const hasFilters = currentSearch || currentAchievement;

  const handleSort = useCallback(
    (column: string) => {
      const direction =
        currentSort?.column === column && currentSort?.direction === 'asc'
          ? 'desc'
          : 'asc';

      // Navigate with new sort params
      const url = new URL(usersPath, window.location.origin);
      url.searchParams.set('sort', column);
      url.searchParams.set('direction', direction);
      if (currentSearch) url.searchParams.set('search', currentSearch);
      if (currentAchievement) url.searchParams.set('achievement', currentAchievement);
      window.location.href = url.toString();
    },
    [currentSort, usersPath, currentSearch, currentAchievement]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const url = new URL(usersPath, window.location.origin);
      if (search) url.searchParams.set('search', search);
      if (achievement) url.searchParams.set('achievement', achievement);
      if (currentSort) {
        url.searchParams.set('sort', currentSort.column);
        url.searchParams.set('direction', currentSort.direction);
      }
      window.location.href = url.toString();
    },
    [search, achievement, usersPath, currentSort]
  );

  const stats = [
    {
      title: labels.totalUsersLabel,
      value: totalUsers,
      icon: 'people',
      iconColor: 'primary',
    },
    {
      title: labels.todayLabel,
      value: usersToday,
      icon: 'calendar-check',
      iconColor: 'success',
    },
    {
      title: labels.thisWeekLabel,
      value: usersThisWeek,
      icon: 'calendar-week',
      iconColor: 'info',
    },
    {
      title: labels.thisMonthLabel,
      value: usersThisMonth,
      icon: 'calendar-month',
      iconColor: 'warning',
    },
    {
      title: labels.activeSubscribersLabel,
      value: activeSubscribers,
      icon: 'star-fill',
      iconColor: 'success',
      borderColor: 'success',
      valueColor: 'success',
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="people"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
      />

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {stats.map((stat, index) => (
          <div key={index} className="col-md-6 col-xl-3">
            <AdminStatCard {...stat} />
          </div>
        ))}
      </div>

      {/* Search and Filter Bar */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <input
                type="text"
                name="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control"
                placeholder={labels.searchPlaceholder}
                autoFocus={!!currentSearch}
              />
            </div>
            <div className="col-md-4">
              <select
                name="achievement"
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                className="form-select"
              >
                <option value="">{labels.allAchievements}</option>
                {achievements.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100">
                {labels.filterButton}
              </button>
            </div>
            {hasFilters && (
              <div className="col-12">
                <a href={usersPath} className="btn btn-sm btn-outline-secondary">
                  <i className="bi bi-x-circle me-1" />
                  {labels.clearFilters}
                </a>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Users Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">
                    <SortableColumn
                      column="id"
                      label={labels.idColumn}
                      currentSort={currentSort}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    <SortableColumn
                      column="email"
                      label={labels.emailColumn}
                      currentSort={currentSort}
                      onSort={handleSort}
                    />
                  </th>
                  <th>{labels.subscriptionColumn}</th>
                  <th>{labels.diaryDaysColumn}</th>
                  <th>{labels.activitiesColumn}</th>
                  <th>
                    <SortableColumn
                      column="created_at"
                      label={labels.registeredColumn}
                      currentSort={currentSort}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="text-end pe-4">{labels.actionsColumn}</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      userPath={userPath(user.id)}
                      labels={labels}
                    />
                  ))
                ) : (
                  <EmptyState search={currentSearch} labels={labels} />
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUsersPage;
